import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Moon, Sparkles, ArrowUp, ArrowDown, Eye, Lightbulb, Loader2, Flame, Droplets, Wind, Mountain } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUITS = {
  wands: { name: 'Bâtons', icon: Flame, color: 'orange', bgColor: 'bg-orange-500' },
  cups: { name: 'Coupes', icon: Droplets, color: 'blue', bgColor: 'bg-blue-500' },
  swords: { name: 'Épées', icon: Wind, color: 'gray', bgColor: 'bg-gray-500' },
  pentacles: { name: 'Deniers', icon: Mountain, color: 'green', bgColor: 'bg-emerald-500' }
};

const MysteriaeCardDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [allCards, setAllCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetchAllCards();
  }, []);

  useEffect(() => {
    if (allCards.length > 0) {
      findCard();
    }
  }, [id, allCards]);

  const fetchAllCards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/cards`);
      const data = await response.json();
      setAllCards(data.cards);
    } catch (error) {
      console.error('Error fetching cards:', error);
    }
  };

  const findCard = () => {
    setLoading(true);
    setImageLoaded(false);
    setImageError(false);
    
    // Try to find by card_id first (for minor arcana like "wands_ace")
    let foundCard = allCards.find(c => c.card_id === id);
    
    // If not found, try by number (for major arcana)
    if (!foundCard) {
      const numId = parseInt(id);
      if (!isNaN(numId)) {
        foundCard = allCards.find(c => c.card_type === 'major' && c.number === numId);
      }
    }
    
    if (foundCard) {
      setCard(foundCard);
    } else {
      navigate('/mysteriae/arcanes');
    }
    setLoading(false);
  };

  const getCurrentIndex = () => {
    if (!card || allCards.length === 0) return -1;
    return allCards.findIndex(c => 
      (c.card_id && c.card_id === card.card_id) || 
      (c.card_type === 'major' && card.card_type === 'major' && c.number === card.number)
    );
  };

  const goToCard = (direction) => {
    const currentIndex = getCurrentIndex();
    if (currentIndex === -1) return;
    
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < allCards.length) {
      const newCard = allCards[newIndex];
      const newId = newCard.card_id || newCard.number;
      navigate(`/mysteriae/arcanes/${newId}`);
    }
  };

  const getSuitInfo = () => {
    if (!card || card.card_type !== 'minor') return null;
    return SUITS[card.suit];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!card) return null;

  const currentIndex = getCurrentIndex();
  const suitInfo = getSuitInfo();
  const SuitIcon = suitInfo?.icon || Moon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/mysteriae/arcanes" 
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
            data-testid="back-to-arcanes"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="font-semibold">Les Arcanes</span>
          </Link>

          {/* Navigation between cards */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToCard(-1)}
              disabled={currentIndex === 0}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 disabled:opacity-30"
              data-testid="prev-card-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span className="text-gray-600 text-sm font-medium">{currentIndex + 1} / {allCards.length}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToCard(1)}
              disabled={currentIndex === allCards.length - 1}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 disabled:opacity-30"
              data-testid="next-card-btn"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Card Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center"
          >
            <div className="relative max-w-sm w-full">
              {/* Loading state */}
              {!imageLoaded && !imageError && (
                <div className={`aspect-[2/3] rounded-2xl animate-pulse flex items-center justify-center ${
                  card.card_type === 'major' ? 'bg-purple-100' :
                  card.suit === 'wands' ? 'bg-orange-100' :
                  card.suit === 'cups' ? 'bg-blue-100' :
                  card.suit === 'swords' ? 'bg-gray-100' :
                  'bg-emerald-100'
                }`}>
                  <SuitIcon className={`h-16 w-16 ${
                    card.card_type === 'major' ? 'text-purple-400' :
                    card.suit === 'wands' ? 'text-orange-400' :
                    card.suit === 'cups' ? 'text-blue-400' :
                    card.suit === 'swords' ? 'text-gray-400' :
                    'text-emerald-400'
                  }`} />
                </div>
              )}
              
              {/* Actual image */}
              {card.image_url && !imageError && (
                <img
                  src={card.image_url}
                  alt={card.name}
                  className={`w-full rounded-2xl shadow-2xl shadow-purple-200/50 border-2 ${
                    card.card_type === 'major' ? 'border-purple-200' :
                    card.suit === 'wands' ? 'border-orange-200' :
                    card.suit === 'cups' ? 'border-blue-200' :
                    card.suit === 'swords' ? 'border-gray-200' :
                    'border-emerald-200'
                  } ${imageLoaded ? 'block' : 'hidden'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    console.log('Image load error for:', card.image_url);
                    setImageError(true);
                  }}
                  data-testid="card-image"
                />
              )}
              
              {/* Fallback when no image or image error */}
              {(!card.image_url || imageError) && (
                <div className={`w-full aspect-[2/3] rounded-2xl shadow-2xl flex items-center justify-center ${
                  card.card_type === 'major' ? 'bg-gradient-to-br from-purple-100 to-indigo-100 border-2 border-purple-200' :
                  card.suit === 'wands' ? 'bg-gradient-to-br from-orange-100 to-amber-100 border-2 border-orange-200' :
                  card.suit === 'cups' ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-2 border-blue-200' :
                  card.suit === 'swords' ? 'bg-gradient-to-br from-gray-100 to-slate-100 border-2 border-gray-200' :
                  'bg-gradient-to-br from-emerald-100 to-green-100 border-2 border-emerald-200'
                }`}>
                  <SuitIcon className={`h-24 w-24 ${
                    card.card_type === 'major' ? 'text-purple-400' :
                    card.suit === 'wands' ? 'text-orange-400' :
                    card.suit === 'cups' ? 'text-blue-400' :
                    card.suit === 'swords' ? 'text-gray-400' :
                    'text-emerald-400'
                  }`} />
                </div>
              )}
              
              {/* Card badge */}
              {card.card_type === 'major' ? (
                <div className="absolute -top-3 -left-3 w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">{card.number}</span>
                </div>
              ) : (
                <div className={`absolute -top-3 -left-3 w-14 h-14 ${suitInfo?.bgColor || 'bg-gray-500'} rounded-full flex items-center justify-center shadow-lg`}>
                  <SuitIcon className="h-7 w-7 text-white" />
                </div>
              )}
            </div>

            {/* Element and Planet/Suit */}
            <div className="flex gap-4 mt-6">
              <div className="px-5 py-3 bg-white rounded-xl border border-purple-200 shadow-sm">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Élément</p>
                <p className="text-purple-700 font-semibold">{card.element}</p>
              </div>
              {card.card_type === 'major' ? (
                <div className="px-5 py-3 bg-white rounded-xl border border-purple-200 shadow-sm">
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Planète/Signe</p>
                  <p className="text-purple-700 font-semibold">{card.planet}</p>
                </div>
              ) : (
                <div className="px-5 py-3 bg-white rounded-xl border border-purple-200 shadow-sm">
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Couleur</p>
                  <p className="text-purple-700 font-semibold">{card.suit_name_fr || suitInfo?.name}</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Card Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Type badge */}
            {card.card_type === 'minor' && (
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${suitInfo?.bgColor || 'bg-gray-500'} text-white text-sm font-medium`}>
                <SuitIcon className="h-4 w-4" />
                Arcane Mineur • {card.suit_name_fr}
              </div>
            )}
            {card.card_type === 'major' && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-600 text-white text-sm font-medium">
                <Moon className="h-4 w-4" />
                Arcane Majeur
              </div>
            )}

            {/* Title */}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="card-title">
                {card.name}
              </h1>
              <p className="text-purple-600 text-lg font-medium">{card.name_en}</p>
            </div>

            {/* Keywords */}
            <div className="flex flex-wrap gap-2">
              {card.keywords.map((keyword, i) => (
                <span
                  key={i}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full border ${
                    card.card_type === 'major' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                    card.suit === 'wands' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                    card.suit === 'cups' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                    card.suit === 'swords' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                    'bg-emerald-100 text-emerald-700 border-emerald-200'
                  }`}
                >
                  {keyword}
                </span>
              ))}
            </div>

            {/* Description */}
            <div className="p-5 bg-white rounded-xl border border-purple-200 shadow-sm">
              <p className="text-gray-700 leading-relaxed">{card.description}</p>
            </div>

            {/* Meanings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Upright */}
              <div className="p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <ArrowUp className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-emerald-700 font-semibold">À l'endroit</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{card.upright_meaning}</p>
              </div>

              {/* Reversed */}
              <div className="p-5 bg-gradient-to-br from-rose-50 to-red-50 rounded-xl border border-rose-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center">
                    <ArrowDown className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-rose-700 font-semibold">Renversé</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{card.reversed_meaning}</p>
              </div>
            </div>

            {/* Symbolism */}
            <div className="p-5 bg-white rounded-xl border border-purple-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Eye className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-gray-900 font-semibold">Symbolisme</h3>
              </div>
              <ul className="space-y-3">
                {card.symbolism.map((symbol, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                    <Sparkles className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed">{symbol}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Advice */}
            <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Lightbulb className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-amber-700 font-semibold">Conseil</h3>
              </div>
              <p className="text-gray-700 italic leading-relaxed">"{card.advice}"</p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Navigation Footer */}
      <footer className="border-t border-purple-100 bg-white/50 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {currentIndex > 0 ? (
              <Button
                variant="outline"
                onClick={() => goToCard(-1)}
                className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                data-testid="footer-prev-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Carte précédente
              </Button>
            ) : (
              <div />
            )}

            <Link
              to="/mysteriae/arcanes"
              className="text-gray-500 hover:text-purple-600 text-sm transition-colors font-medium"
            >
              Retour à la liste
            </Link>

            {currentIndex < allCards.length - 1 ? (
              <Button
                variant="outline"
                onClick={() => goToCard(1)}
                className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                data-testid="footer-next-btn"
              >
                Carte suivante
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MysteriaeCardDetailPage;
